import React from 'react';
import { useNavigate } from 'react-router-dom';

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <div className="page-content">
      <div className="policy-page">
        <button 
          className="btn outline" 
          onClick={() => navigate(-1)}
          style={{ marginBottom: '2rem' }}
        >
          ← Back
        </button>
        
        <div className="policy-header">
          <h1 className="policy-title">🧡 About NeoTeeForge</h1>
          <p className="policy-date">Building better habits. One day at a time.</p>
        </div>
        
        <div className="policy-content">
          <div className="policy-section">
            <div style={{
              textAlign: 'center',
              marginBottom: '3rem',
              padding: '2rem',
              background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
              borderRadius: '16px'
            }}>
              <h2 style={{ color: '#92400e', marginBottom: '1rem' }}>Our Mission</h2>
              <p style={{ fontSize: '1.2rem', lineHeight: '1.6' }}>
                To create meaningful products that promote learning, creativity, and healthy living through beautifully designed digital and physical experiences.
              </p>
            </div>
          </div>

          <div className="policy-section">
            <h3>🌟 Our Story</h3>
            <p>NeoTeeForge was born from a simple idea: <strong>small habits create big impact</strong>. What started as a passion project to make learning fun and accessible has grown into a creative brand dedicated to helping individuals and families build better routines and develop healthy habits.</p>
            
            <p>Our founder, an educator and designer, noticed how traditional learning methods often felt stressful and disconnected from real life. This inspired us to create products that bridge the gap between learning and living, making personal growth an enjoyable, integrated part of daily life.</p>
          </div>

          <div className="policy-section">
            <h3>💡 Our Core Beliefs</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1.5rem',
              marginTop: '1.5rem'
            }}>
              <div style={{
                background: '#f0f9ff',
                padding: '1.5rem',
                borderRadius: '12px',
                borderLeft: '4px solid #0ea5e9'
              }}>
                <h4 style={{ color: '#0369a1', marginTop: 0 }}>Learning Should Be Fun</h4>
                <p>We believe education doesn't have to be boring or stressful. Our products are designed to make learning an engaging, enjoyable experience.</p>
              </div>
              
              <div style={{
                background: '#f0fdf4',
                padding: '1.5rem',
                borderRadius: '12px',
                borderLeft: '4px solid #10b981'
              }}>
                <h4 style={{ color: '#059669', marginTop: 0 }}>Small Steps, Big Changes</h4>
                <p>Lasting transformation comes from consistent daily habits, not drastic overnight changes. Our products support gradual, sustainable growth.</p>
              </div>
              
              <div style={{
                background: '#fef3c7',
                padding: '1.5rem',
                borderRadius: '12px',
                borderLeft: '4px solid #f59e0b'
              }}>
                <h4 style={{ color: '#92400e', marginTop: 0 }}>Quality Over Quantity</h4>
                <p>Every NeoTeeForge product is carefully crafted with attention to detail, ensuring real-life usability and lasting value.</p>
              </div>
            </div>
          </div>

          <div className="policy-section">
            <h3>📚 Our Products</h3>
            <p>At NeoTeeForge, we specialize in creating thoughtfully designed products that serve real purposes:</p>
            
            <div style={{
              background: '#f8fafc',
              padding: '2rem',
              borderRadius: '12px',
              marginTop: '1rem'
            }}>
              <h4 style={{ color: '#7c3aed', marginTop: 0 }}>For Kids & Families</h4>
              <p>Our children's products are designed to:</p>
              <ul className="policy-list">
                <li><strong>Encourage healthy habits</strong> through fun routines</li>
                <li><strong>Spark creativity</strong> with engaging activities</li>
                <li>Promote <strong>screen-free learning</strong> and play</li>
                <li>Foster <strong>parent-child engagement</strong> and bonding</li>
                <li>Develop <strong>life skills</strong> through age-appropriate challenges</li>
              </ul>
            </div>
            
            <div style={{
              background: '#fef7ff',
              padding: '2rem',
              borderRadius: '12px',
              marginTop: '1.5rem'
            }}>
              <h4 style={{ color: '#a855f7', marginTop: 0 }}>Digital Products</h4>
              <ul className="policy-list">
                <li><strong>Educational eBooks</strong> that make learning engaging</li>
                <li><strong>Printable templates</strong> for planners and organizers</li>
                <li><strong>Activity books</strong> that stimulate creativity</li>
                <li><strong>Learning resources</strong> for various age groups</li>
                <li><strong>Habit trackers</strong> for personal development</li>
              </ul>
            </div>
            
            <div style={{
              background: '#f0f9ff',
              padding: '2rem',
              borderRadius: '12px',
              marginTop: '1.5rem'
            }}>
              <h4 style={{ color: '#0ea5e9', marginTop: 0 }}>Physical Products</h4>
              <ul className="policy-list">
                <li><strong>Curated book collections</strong> for different interests</li>
                <li><strong>Quality merchandise</strong> that reflects our values</li>
                <li><strong>Educational tools</strong> and learning aids</li>
                <li><strong>Gift sets</strong> for special occasions</li>
              </ul>
            </div>
          </div>

          <div className="policy-section">
            <h3>🎯 What Makes Us Different</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1rem',
              marginTop: '1.5rem'
            }}>
              <div style={{ textAlign: 'center', padding: '1.5rem' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: '#fef3c7',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  color: '#92400e',
                  fontSize: '1.5rem'
                }}>
                  ✨
                </div>
                <h4 style={{ margin: '0.5rem 0' }}>Thoughtful Design</h4>
                <p>Every product is created with care, simplicity, and real-life usability in mind.</p>
              </div>
              
              <div style={{ textAlign: 'center', padding: '1.5rem' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: '#dbeafe',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  color: '#1e40af',
                  fontSize: '1.5rem'
                }}>
                  ❤️
                </div>
                <h4 style={{ margin: '0.5rem 0' }}>Customer-Centric</h4>
                <p>Your feedback shapes our products. We listen and adapt to meet your needs.</p>
              </div>
              
              <div style={{ textAlign: 'center', padding: '1.5rem' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  background: '#dcfce7',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  color: '#166534',
                  fontSize: '1.5rem'
                }}>
                  🌱
                </div>
                <h4 style={{ margin: '0.5rem 0' }}>Sustainable Growth</h4>
                <p>We believe in products that grow with you and support long-term development.</p>
              </div>
            </div>
          </div>

          <div className="policy-section">
            <h3>👥 Our Team</h3>
            <p>NeoTeeForge is powered by a small but passionate team of:</p>
            
            <div className="about-team">
              <div className="team-member">
                <div style={{
                  width: '100px',
                  height: '100px',
                  background: 'linear-gradient(135deg, #e63946, #d00000)',
                  borderRadius: '50%',
                  margin: '0 auto 1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '2rem',
                  fontWeight: 'bold'
                }}>
                  S9
                </div>
                <h4>Seo9ine</h4>
                <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Founder & Creative Director</p>
                <p>Passionate about making learning accessible and enjoyable for everyone.</p>
              </div>
              
              <div className="team-member">
                <div style={{
                  width: '100px',
                  height: '100px',
                  background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                  borderRadius: '50%',
                  margin: '0 auto 1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '2rem'
                }}>
                  📚
                </div>
                <h4>Content Creators</h4>
                <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Educators & Writers</p>
                <p>Experts who craft engaging, age-appropriate content for our products.</p>
              </div>
              
              <div className="team-member">
                <div style={{
                  width: '100px',
                  height: '100px',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  borderRadius: '50%',
                  margin: '0 auto 1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '2rem'
                }}>
                  🎨
                </div>
                <h4>Design Team</h4>
                <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Designers & Illustrators</p>
                <p>Creative minds who make our products visually appealing and user-friendly.</p>
              </div>
            </div>
          </div>

          <div className="policy-section">
            <h3>🤝 Our Commitment to You</h3>
            <div style={{
              background: 'linear-gradient(135deg, #e0f2fe, #f0f9ff)',
              padding: '2rem',
              borderRadius: '12px',
              marginTop: '1rem'
            }}>
              <ul className="policy-list">
                <li><strong>Quality Assurance:</strong> Every product undergoes rigorous testing</li>
                <li><strong>Customer Support:</strong> We're here to help you succeed</li>
                <li><strong>Continuous Improvement:</strong> We regularly update and improve our products</li>
                <li><strong>Ethical Practices:</strong> Fair pricing and honest marketing</li>
                <li><strong>Community Building:</strong> Creating spaces for learning and sharing</li>
              </ul>
            </div>
          </div>

          <div className="policy-section">
            <h3>🌍 Join Our Community</h3>
            <p>NeoTeeForge is more than just a brand—it's a community of learners, creators, and families committed to personal growth. Join us on this journey:</p>
            
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginTop: '1.5rem',
              flexWrap: 'wrap'
            }}>
              <button 
                className="btn primary"
                onClick={() => navigate('/')}
              >
                Explore Our Products
              </button>
              
              <button 
                className="btn outline"
                onClick={() => navigate('/contact-us')}
              >
                Get in Touch
              </button>
              
              <button 
                className="btn outline"
                onClick={() => {
                  // Scroll to newsletter section
                  navigate('/');
                  setTimeout(() => {
                    document.querySelector('.modern-newsletter')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
              >
                Join Newsletter
              </button>
            </div>
          </div>

          <div style={{
            marginTop: '3rem',
            paddingTop: '2rem',
            borderTop: '1px solid #e2e8f0',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
            padding: '2rem',
            borderRadius: '12px'
          }}>
            <h3 style={{ color: '#92400e' }}>✨ Our Promise</h3>
            <p style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
              We promise to continue creating products that inspire, educate, and support you in building the life you want—one healthy habit at a time.
            </p>
            <p style={{ marginTop: '1rem', fontStyle: 'italic', color: '#92400e' }}>
              Thank you for being part of the NeoTeeForge family! 🧡
            </p>
          </div>

          <div style={{
            marginTop: '2rem',
            padding: '1.5rem',
            background: '#f8fafc',
            borderRadius: '12px',
            fontSize: '0.9rem',
            color: '#64748b'
          }}>
            <p><strong>Contact Information:</strong></p>
            <p>📧 Email: support@neoteeforge.com | 🌐 Website: https://shop.neoteeforge.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;